import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import { useCurrentUser } from '../../shared/hooks/useCurrentUser'
import { HIDDEN_PLAN_KEYS } from '../../shared/constants/planLabels'
import { USAGE_TYPES } from './featureToggleConstants'
import { sortByName } from './featureToggleUtils'
import FeatureToggleTable from './components/FeatureToggleTable'
import FeatureActionsMenu from './components/FeatureActionsMenu'
import TenantOperationDialog from './components/TenantOperationDialog'
import TenantListDialog from './components/TenantListDialog'
import EditFeatureDialog from './components/EditFeatureDialog'
import CreateFeatureDialog from './components/CreateFeatureDialog'
import FeatureToggleSnackbar from './components/FeatureToggleSnackbar'
import {
  useFeatureTogglesQuery,
  useCreateFeatureToggleMutation,
  useUpdateFeatureToggleMutation,
} from './useFeatureToggle'
import { useFeatureTenantActions } from './useFeatureTenantActions'

function matchesSearch(feature, query) {
  const normalizedQuery = query.trim().toLowerCase()
  if (normalizedQuery === '') return true

  return (
    feature.name.toLowerCase().includes(normalizedQuery) ||
    (feature.description ?? '').toLowerCase().includes(normalizedQuery)
  )
}

function isSuccess(status) {
  return status >= 200 && status < 300
}

function FeatureToggleAdminPage() {
  const navigate = useNavigate()
  const { allowedToManageFeature } = useCurrentUser()
  const { features, isLoading } = useFeatureTogglesQuery('ALL', { enabled: allowedToManageFeature })
  const createMutation = useCreateFeatureToggleMutation()
  const updateMutation = useUpdateFeatureToggleMutation()
  const {
    activeFeature,
    openMenu,
    closeMenu,
    showToast,
    menuProps,
    tenantDialogProps,
    listDialogProps,
    snackbar,
    handleSnackbarClose,
    busy: tenantActionsBusy,
  } = useFeatureTenantActions()

  const [searchQuery, setSearchQuery] = useState('')

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editDescription, setEditDescription] = useState('')
  const [editUsageType, setEditUsageType] = useState(USAGE_TYPES[0])
  const [editPlans, setEditPlans] = useState([])

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createUsageType, setCreateUsageType] = useState(USAGE_TYPES[0])
  const [createPlans, setCreatePlans] = useState([])

  function openEditDialog() {
    closeMenu()
    setEditDescription(activeFeature.description ?? '')
    setEditUsageType(activeFeature.usageType ?? USAGE_TYPES[0])
    setEditPlans((activeFeature.plans ?? []).filter((plan) => !HIDDEN_PLAN_KEYS.includes(plan)))
    setEditDialogOpen(true)
  }

  function openCreateDialog() {
    setCreateName('')
    setCreateDescription('')
    setCreateUsageType(USAGE_TYPES[0])
    setCreatePlans([])
    setCreateDialogOpen(true)
  }

  async function submitEditFeature() {
    const preservedHiddenPlans = (activeFeature.plans ?? []).filter((plan) =>
      HIDDEN_PLAN_KEYS.includes(plan),
    )
    const plans = [...editPlans, ...preservedHiddenPlans]

    setEditDialogOpen(false)
    try {
      const response = await updateMutation.mutateAsync({
        name: activeFeature.name,
        description: editDescription,
        plans,
        usageType: editUsageType,
      })
      showToast(
        isSuccess(response.status) ? 'success' : 'error',
        isSuccess(response.status) ? 'Feature atualizada.' : 'Ocorreu um problema. Verifique os dados.',
      )
    } catch {
      showToast('error', 'Ocorreu um problema. Verifique os dados.')
    }
  }

  async function submitCreateFeature() {
    setCreateDialogOpen(false)
    try {
      const response = await createMutation.mutateAsync({
        name: createName,
        description: createDescription,
        plans: createPlans,
        usageType: createUsageType,
      })
      showToast(
        isSuccess(response.status) ? 'success' : 'error',
        isSuccess(response.status) ? 'Feature criada.' : 'Ocorreu um problema. Verifique os dados.',
      )
    } catch {
      showToast('error', 'Ocorreu um problema. Verifique os dados.')
    } finally {
      setCreateName('')
      setCreateDescription('')
      setCreateUsageType(USAGE_TYPES[0])
      setCreatePlans([])
    }
  }

  if (!allowedToManageFeature) {
    return (
      <Box>
        <Typography variant="h5" component="h2" gutterBottom>
          Administração de Features
        </Typography>
        <Typography>Sem permissão para acessar esta página.</Typography>
      </Box>
    )
  }

  const busy = tenantActionsBusy || createMutation.isPending || updateMutation.isPending
  const sortedFeatures = sortByName(features).filter((item) => matchesSearch(item, searchQuery))

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            aria-label="Voltar"
            onClick={() => navigate('/workspace/feature-toggle')}
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h2">
            Administração de Features
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          Nova feature
        </Button>
      </Box>

      <TextField
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder="Buscar por nome ou descrição"
        fullWidth
        margin="normal"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <FeatureToggleTable features={sortedFeatures} isLoading={isLoading} onOpenMenu={openMenu} />

      <FeatureActionsMenu {...menuProps} onEditFeature={openEditDialog} />
      <TenantOperationDialog {...tenantDialogProps} />
      <TenantListDialog {...listDialogProps} />

      <EditFeatureDialog
        open={editDialogOpen}
        featureName={activeFeature?.name}
        description={editDescription}
        usageType={editUsageType}
        plans={editPlans}
        onDescriptionChange={setEditDescription}
        onUsageTypeChange={setEditUsageType}
        onPlansChange={setEditPlans}
        onCancel={() => setEditDialogOpen(false)}
        onSubmit={submitEditFeature}
      />

      <CreateFeatureDialog
        open={createDialogOpen}
        name={createName}
        description={createDescription}
        usageType={createUsageType}
        plans={createPlans}
        onNameChange={setCreateName}
        onDescriptionChange={setCreateDescription}
        onUsageTypeChange={setCreateUsageType}
        onPlansChange={setCreatePlans}
        onCancel={() => setCreateDialogOpen(false)}
        onSubmit={submitCreateFeature}
      />

      {busy && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <FeatureToggleSnackbar snackbar={snackbar} onClose={handleSnackbarClose} />
    </Box>
  )
}

export default FeatureToggleAdminPage
